import React, { useEffect, useState, useCallback } from 'react';
import { Select as AntSelect, Alert, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useGet, useAuth, usePatch } from 'hooks';
import {
  Select,
  Detail,
  Loader,
  Radio,
  Tooltip,
  Icon,
} from 'components/common';
import { userAlerts } from 'services/api';
import { getAlertToCreate } from 'selectors/alerts';
import { setAlertToCreate } from 'actions/alerts';
import {
  frequencyEnums,
  frequencyOptions,
  frequencyRadioOptions,
  frequencyRadioOptionsLimited,
} from 'models/frequency';
import { UpgradeLicense } from 'utils/constants';
import Share from './Share';
import { searchOrigin } from 'components/Search/Results/constants';
import { getListsFilters } from './helpers';

const { Option } = AntSelect;

const EditSearch = ({
  sanityCheckLoading,
  sanityError,
  recentItemsResults,
  allUsersLoading,
  userOptions,
  userAccounts,
  checkUserPermissions,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const activeAlert = useSelector(getAlertToCreate);
  const { user } = useAuth();

  const [selectedAlertId, setSelectedAlertId] = useState(null);
  const [localAlert, setLocalAlert] = useState(null);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [showWarning, setShowWarning] = useState('');

  const [{ res: existingAlerts, loading: alertsLoading }] = useGet({
    url: userAlerts,
    query: {
      searchOrigins: [activeAlert.searchOrigin],
    },
    onMount: true,
  });

  const [{ res: success, loading: updating, error }, updateAlert] = usePatch({
    url: userAlerts,
  });

  useEffect(() => {
    if (success) {
      onSuccess(localAlert, true);
      dispatch(setAlertToCreate());
    }
  }, [success, dispatch, onSuccess, localAlert]);

  const handleSelectAlert = id => {
    setSelectedAlertId(id);
    const alert = existingAlerts.find(a => a.id === id);
    if (alert) {
      const frequency = frequencyOptions.find(
        f => f.enumValue === alert.frequency
      );
      const sharedFrequency = frequencyOptions.find(
        f => f.enumValue === alert.sharedFrequency
      );

      const transformedAlert = {
        ...alert,
        recentItemsResults: recentItemsResults,
        frequency: frequency
          ? frequency.value
          : frequencyOptions.find(f => f.enumValue === frequencyEnums.Monthly)
              .value,
        frequencyRadio: frequencyRadioOptions[!alert.frequency ? 0 : 1].label,
        childAlert: {
          users: (alert.sharedWith || []).concat(
            alert.sharedWithInactive || []
          ),
          frequency: sharedFrequency
            ? sharedFrequency.value
            : frequencyOptions.find(f => f.enumValue === frequencyEnums.Monthly)
                .value,
          frequencyRadio:
            frequencyRadioOptions[!alert.sharedFrequency ? 0 : 1].label,
        },
      };

      setLocalAlert(transformedAlert);
      const users = userAccounts
        .filter(u =>
          transformedAlert.childAlert.users.some(
            u2 => u2.userId === u.legacyUserId && u.legacyUserId !== user.userId
          )
        )
        .map(u =>
          userOptions.some(x => x.userId === u.legacyUserId)
            ? u.legacyUserId
            : u.fullname
        );
      handleUsersChange(users);
    }
  };

  const handleLocalAlertChange = useCallback(
    key => value => {
      setLocalAlert(prev => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const handleChildAlertChange = useCallback(
    key => value => {
      setLocalAlert(prev => ({
        ...prev,
        childAlert: {
          ...prev.childAlert,
          [key]: value,
        },
      }));
    },
    []
  );

  const handleUsersChange = useCallback(
    v => {
      const childAlertUsers = v.map(val =>
        typeof val === 'number'
          ? val
          : userAccounts.find(y => y.fullname === val).legacyUserId
      );
      handleChildAlertChange('users')(childAlertUsers);
      setSharedUsers(v);

      const warning = checkUserPermissions(childAlertUsers);
      setShowWarning(warning);
    },
    [handleChildAlertChange, checkUserPermissions, userAccounts]
  );

  const handleUpdate = () => {
    if (!localAlert) return;

    const frequency =
      localAlert.frequencyRadio === 'Alert'
        ? frequencyOptions.find(freq => freq.value === localAlert.frequency)
            ?.enumValue || frequencyEnums.Weekly // Fallback if matching fails
        : frequencyEnums.NoAlert;

    const childFrequency =
      localAlert.childAlert?.frequencyRadio === 'Alert'
        ? frequencyOptions.find(
            freq => freq.value === localAlert.childAlert.frequency
          )?.enumValue || frequencyEnums.Weekly
        : frequencyEnums.NoAlert;

    const payload = {
      ...localAlert,
      monthlyKeywords: activeAlert.monthlyKeywords,
      highlightQuery: activeAlert.highlightQuery,
      structuredSearch: activeAlert.structuredSearch,
      ...getListsFilters(activeAlert),
      nestedQueries: activeAlert.nestedQueries,
      excludeRecentlyExportedEmails: activeAlert.excludeRecentlyExportedEmails,
      // Current session at Save Search click — not the selected row’s old flag
      isAiGenerated: Boolean(activeAlert.isAiGenerated),

      frequency,
      childAlert: {
        ...localAlert.childAlert,
        frequency: childFrequency,
      },
    };

    updateAlert(payload);
  };

  if (!activeAlert) return null;

  const alertOptions =
    existingAlerts?.map(a => ({
      label: a.name,
      value: a.id,
      searchOrigin: a.searchOrigin,
      owner: a.owner,
      ownerId: a.ownerId,
    })) || [];

  // Default values for disabled state
  const displayAlert = localAlert || {
    frequencyRadio: 'No Alert',
    frequency: frequencyOptions.find(
      f => f.enumValue === frequencyEnums.Monthly
    ).value,
    childAlert: {
      frequencyRadio: 'No Alert',
      frequency: frequencyOptions.find(
        f => f.enumValue === frequencyEnums.Monthly
      ).value,
    },
    structuredSearch: activeAlert.structuredSearch,
  };

  const isDisabled = !localAlert;

  return (
    <>
      {sanityCheckLoading || allUsersLoading || alertsLoading ? (
        <Loader />
      ) : sanityError ? (
        <div />
      ) : (
        <div>
          <Detail label='Select Saved Search'>
            <Select
              blurOnChange={true}
              optionLabelProp='label'
              options={alertOptions}
              value={selectedAlertId}
              onChange={handleSelectAlert}
              prefix={
                activeAlert.searchOrigin === searchOrigin.researcherSearch
                  ? 'user'
                  : activeAlert.searchOrigin === searchOrigin.organisationSearch
                  ? 'company'
                  : 'searchDollar'
              }
              placeholder={
                activeAlert.searchOrigin === searchOrigin.researcherSearch
                  ? 'Select a Contact saved search or alert to update'
                  : activeAlert.searchOrigin === searchOrigin.organisationSearch
                  ? 'Select an Organisation saved search or alert to update'
                  : 'Select a Purchasing saved search or alert to update'
              }
              style={{ width: '100%' }}
              renderOption={o => (
                <Option
                  key={o.value}
                  disabled={o.ownerId !== user.userId}
                  value={o.value}
                  label={o.label}
                >
                  <Tooltip
                    title={
                      o.ownerId !== user.userId
                        ? 'Cannot update shared search'
                        : ''
                    }
                    placement='right'
                  >
                    <div
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <Icon
                        size='sm'
                        icon={
                          o.searchOrigin === searchOrigin.researcherSearch
                            ? 'user'
                            : o.searchOrigin === searchOrigin.organisationSearch
                            ? 'company'
                            : 'searchDollar'
                        }
                        color='gray'
                        style={{ marginRight: '8px' }}
                      />
                      {o.label}
                    </div>
                  </Tooltip>
                </Option>
              )}
            />
          </Detail>

          <>
            {!user.disableExport && (
              <Detail style={{ marginTop: '21px', marginBottom: '21px' }}>
                <Radio
                  radioStyle={{ height: '30px', lineHeight: '30px' }}
                  value={displayAlert.frequencyRadio}
                  onChange={handleLocalAlertChange('frequencyRadio')}
                  options={
                    user.isPartnerAccount
                      ? frequencyRadioOptionsLimited
                      : frequencyRadioOptions
                  }
                  disabled={isDisabled}
                />
                <Tooltip
                  title={user.isPartnerAccount ? <UpgradeLicense /> : ''}
                >
                  <Select
                    disabled={
                      isDisabled ||
                      updating ||
                      displayAlert.frequencyRadio !== 'Alert'
                    }
                    value={displayAlert.frequency}
                    onChange={handleLocalAlertChange('frequency')}
                    options={frequencyOptions}
                    style={{ minWidth: '95px' }}
                    disableTooltip
                  />
                </Tooltip>
              </Detail>
            )}
            <Share
              userOptions={userOptions}
              activeAlert={displayAlert}
              sharedUsers={sharedUsers}
              handleUsersChange={handleUsersChange}
              showWarning={showWarning}
              loading={updating}
              handleChildAlertChange={handleChildAlertChange}
              disabled={isDisabled}
            />
          </>
        </div>
      )}

      {sanityError && (
        <Alert
          message={
            'This is not a valid search, please correct search before saving again.'
          }
          type='error'
        />
      )}
      {error && (
        <Alert
          message={
            error.message?.message ??
            'Something went wrong. If the issue persists contact your Customer Success Manager.'
          }
          type='error'
        />
      )}
      <div
        style={{
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        <Button
          onClick={() => dispatch(setAlertToCreate())}
          disabled={updating}
          data-testid='update-cancel-button'
        >
          Cancel
        </Button>
        <Button
          type='primary'
          onClick={handleUpdate}
          disabled={updating || isDisabled}
          hidden={sanityCheckLoading || sanityError}
          data-testid='update-save-button'
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default EditSearch;
